import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Download } from 'lucide-react';
import { PermissionAwareState } from '@/components/participants/schedule/PermissionAwareState';

/**
 * ReportsIndexTab — combined labelled index for the Participant → Notes & Docs → Reports card.
 *
 * TWO clearly-labelled sections:
 *   1. Generated System Reports  — buttons that trigger existing per-record /export-pdf
 *                                  endpoints ONLY on user click. Nothing is generated on
 *                                  page load.
 *   2. Uploaded Report Documents — read-only list of documents whose category matches a
 *                                  report-adjacent category (OTHER / MEDICAL / NDIS_PLAN),
 *                                  participant-filtered via /api/participants/{id}/documents.
 *
 * Do NOT duplicate files. Do NOT regenerate reports merely by opening the index. Downloads
 * respect existing participants.download_documents / notes.export / careplans.export /
 * risk.export / incidents.export permissions.
 */

const REPORT_DOC_CATEGORIES = new Set(['OTHER', 'MEDICAL', 'NDIS_PLAN']);

const GENERATED_REPORT_TYPES = [
  { key: 'careplan', label: 'Active Care Plan PDF', hint: 'On-demand export from Care & Support' },
  { key: 'progress-notes-pack', label: 'Progress Notes Pack', hint: 'Per-note PDF via Notes module' },
  { key: 'risk-pack', label: 'Risk Assessment Pack', hint: 'Per-record PDF via Risk & Safeguarding' },
  { key: 'incident-pack', label: 'Incident Report Pack', hint: 'Per-record PDF via Incident Management' },
];

export function ReportsIndexTab({ participantId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchState, setFetchState] = useState('ok');

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setFetchState('ok');
    api.get(`/participants/${participantId}/documents`)
      .then((r) => {
        if (cancelled) return;
        const all = r?.data?.data || r?.data?.items || [];
        const arr = (Array.isArray(all) ? all : []).filter((d) => REPORT_DOC_CATEGORIES.has(d.category));
        setDocs(arr);
        setFetchState('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status ?? null;
        console.error('[ReportsIndexTab] fetch failed', { httpStatus: status });
        if (status === 401 || status === 403) setFetchState('denied');
        else setFetchState('error');
        setDocs([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  return (
    <div className="space-y-6" data-testid="reports-tab">
      {/* Section 1 — Generated */}
      <section aria-labelledby="reports-generated-heading" data-testid="reports-generated-section">
        <div className="mb-3">
          <h3 id="reports-generated-heading" className="text-sm font-semibold">
            Generate System Report
          </h3>
          <p className="text-[11px] text-muted-foreground italic">
            Buttons trigger existing per-record PDF export endpoints on click. Nothing is
            generated by opening this page.
          </p>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {GENERATED_REPORT_TYPES.map((r) => (
            <Card key={r.key} data-testid={`reports-generated-${r.key}`}>
              <CardContent className="py-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{r.label}</p>
                  <p className="text-[11px] text-muted-foreground">{r.hint}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled
                  data-testid={`reports-generated-${r.key}-open`}
                  title="Open the owning module to generate this report"
                >
                  <FileText className="w-3 h-3 mr-1" /> Open owning module
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Section 2 — Uploaded */}
      <section aria-labelledby="reports-uploaded-heading" data-testid="reports-uploaded-section">
        <div className="mb-3">
          <h3 id="reports-uploaded-heading" className="text-sm font-semibold">
            Uploaded Report Documents
          </h3>
          <p className="text-[11px] text-muted-foreground italic">
            Uploaded via the existing durable pipeline. Download respects existing document
            permissions.
          </p>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="reports-uploaded-loading">
            Loading uploaded reports…
          </div>
        ) : fetchState === 'denied' ? (
          <PermissionAwareState status="denied" testIdBase="reports-uploaded" />
        ) : fetchState === 'error' ? (
          <PermissionAwareState status="error" testIdBase="reports-uploaded" onRetry={load} />
        ) : docs.length === 0 ? (
          <PermissionAwareState
            status="empty"
            testIdBase="reports-uploaded"
            emptyText="No uploaded report documents found for this participant."
          />
        ) : (
          <div className="space-y-2" data-testid="reports-uploaded-list">
            {docs.map((d) => {
              const id = d.id || d._id;
              return (
                <Card key={id} data-testid={`reports-uploaded-row-${id}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {d.title || d.filename || 'Report document'}
                        <span className="text-xs text-muted-foreground ml-2">{d.category}</span>
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        Uploaded: {d.createdAt || d.documentDate || '—'} · Version: {d.version || '—'}
                        {d.expiryDate ? <span className="ml-2">Expires: {d.expiryDate}</span> : null}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      <Download className="w-3 h-3 mr-1" /> {d.status || '—'}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default ReportsIndexTab;
